package com.example.positionahead.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.positionahead.model.*
import com.example.positionahead.ui.theme.gainColor
import com.example.positionahead.ui.theme.lossColor
import com.example.positionahead.util.formatCurrency
import com.example.positionahead.util.formatGainLoss
import com.example.positionahead.util.formatNumber
import com.example.positionahead.util.formatPercent
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.KeyboardArrowDown

@Composable
fun CollapsibleSection(
    title: String,
    expanded: Boolean,
    onToggle: () -> Unit,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    val rotation by animateFloatAsState(if (expanded) 180f else 0f)
    Column(modifier = modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(onClick = onToggle)
                .padding(vertical = 16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Black,
                letterSpacing = 0.5.sp
            )
            Icon(
                imageVector = Icons.Default.KeyboardArrowDown,
                contentDescription = null,
                modifier = Modifier.rotate(rotation).size(24.dp),
                tint = MaterialTheme.colorScheme.primary
            )
        }
        AnimatedVisibility(visible = expanded) {
            Column(
                modifier = Modifier.padding(bottom = 16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                content()
            }
        }
    }
}

@Composable
fun PositionRow(
    snapshot: PositionSnapshot,
    analysis: PositionAnalysis?,
    expanded: Boolean,
    onToggleExpand: () -> Unit,
    onRemove: () -> Unit,
    onAddLot: (shares: Double, costBasis: Double, purchaseDate: String?, note: String?) -> Unit,
    onUpdateLot: (lotId: String, shares: Double, costBasis: Double, purchaseDate: String?, note: String?) -> Unit,
    onSellLot: (lotId: String, sharesToSell: Double) -> Unit,
    modifier: Modifier = Modifier,
) {
    val hasMarketPrice = (snapshot.quote?.price ?: 0.0) > 0.0
    val isEstimatedPrice = hasMarketPrice && !snapshot.hasLivePrice
    val changeColor = when {
        !hasMarketPrice -> MaterialTheme.colorScheme.onSurfaceVariant
        snapshot.unrealizedGainLoss >= 0 -> gainColor
        else -> lossColor
    }
    
    val signalColor = when(analysis?.signal) {
        PositionSignal.SELL, PositionSignal.REDUCE -> lossColor
        PositionSignal.REVIEW -> Color(0xFFF59E0B)
        PositionSignal.WATCH -> Color(0xFF3B82F6)
        else -> gainColor
    }

    var showAddLot by remember { mutableStateOf(false) }
    var editingLotId by remember { mutableStateOf<String?>(null) }
    var sellingLotId by remember { mutableStateOf<String?>(null) }
    var showDeleteConfirm by remember { mutableStateOf(false) }

    Column(
        modifier = modifier
            .fillMaxWidth()
            .clickable(onClick = onToggleExpand)
            .padding(horizontal = 20.dp, vertical = 16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(
                        text = snapshot.position.symbol,
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Black,
                        letterSpacing = (-0.5).sp
                    )
                    analysis?.let {
                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(4.dp))
                                .background(signalColor.copy(alpha = 0.1f))
                                .padding(horizontal = 6.dp, vertical = 2.dp)
                        ) {
                            Text(it.signal.name, color = signalColor, style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Black)
                        }
                    }
                }
                Text(
                    text = buildString {
                        append("${formatNumber(snapshot.position.totalShares(), 2)} shares")
                        if (isEstimatedPrice) append(" · Est. price")
                    },
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    fontWeight = FontWeight.Medium,
                )
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(
                    text = if (hasMarketPrice) formatCurrency(snapshot.marketValue) else "—",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                )
                Box(
                    modifier = Modifier
                        .padding(top = 4.dp)
                        .clip(RoundedCornerShape(6.dp))
                        .background(changeColor.copy(alpha = 0.1f))
                        .padding(horizontal = 6.dp, vertical = 2.dp)
                ) {
                    Text(
                        text = formatGainLoss(
                            snapshot.unrealizedGainLoss,
                            snapshot.unrealizedGainLossPercent,
                            hasMarketPrice,
                        ),
                        style = MaterialTheme.typography.labelSmall,
                        color = changeColor,
                        fontWeight = FontWeight.Black
                    )
                }
            }
        }

        AnimatedVisibility(visible = expanded) {
            Column(
                modifier = Modifier
                    .padding(top = 20.dp)
                    .clip(RoundedCornerShape(20.dp))
                    .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f))
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                // Proactive Analysis Section
                analysis?.let { ana ->
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        if (ana.reasonsToHold.isNotEmpty()) {
                            AnalysisCard(title = "Why you're holding it", items = ana.reasonsToHold, color = gainColor)
                        }
                        if (ana.thingsToWatch.isNotEmpty()) {
                            AnalysisCard(title = "Things to watch", items = ana.thingsToWatch, color = Color(0xFFF59E0B))
                        }
                        if (ana.sellTriggers.isNotEmpty()) {
                            AnalysisCard(title = "Sell/Reduce triggers", items = ana.sellTriggers, color = lossColor)
                        }
                        
                        ana.sellCase?.let { sc ->
                            SellCaseSection(sc)
                        }
                    }
                    HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        DetailItem("Average Cost", formatCurrency(snapshot.position.averageCostBasisPerShare()))
                        DetailItem("Total Cost", formatCurrency(snapshot.costBasis))
                    }
                    Column(horizontalAlignment = Alignment.End, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        snapshot.quote?.let { quote ->
                            DetailItem("Market Price", formatCurrency(quote.price), Alignment.End)
                        }
                        DetailItem("Allocation", formatPercent(snapshot.portfolioWeightPercent), Alignment.End)
                    }
                }

                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
                
                Row(
                    modifier = Modifier.fillMaxWidth(), 
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    TextButton(
                        onClick = { showAddLot = true; editingLotId = null; sellingLotId = null },
                        modifier = Modifier.weight(1f).clip(RoundedCornerShape(12.dp)).background(MaterialTheme.colorScheme.primary.copy(alpha = 0.1f))
                    ) {
                        Text("Add Lot", fontWeight = FontWeight.Bold)
                    }
                    TextButton(
                        onClick = { showDeleteConfirm = true },
                        modifier = Modifier.weight(1f).clip(RoundedCornerShape(12.dp)).background(MaterialTheme.colorScheme.error.copy(alpha = 0.1f))
                    ) {
                        Text("Close Position", color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold)
                    }
                }

                if (showDeleteConfirm) {
                    AlertDialog(
                        onDismissRequest = { showDeleteConfirm = false },
                        title = { Text("Close ${snapshot.position.symbol}?", fontWeight = FontWeight.Black) },
                        text = {
                            Text("This will permanently remove all history for this holding.")
                        },
                        confirmButton = {
                            TextButton(onClick = { showDeleteConfirm = false; onRemove() }) {
                                Text("Confirm", color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold)
                            }
                        },
                        dismissButton = {
                            TextButton(onClick = { showDeleteConfirm = false }) {
                                Text("Cancel")
                            }
                        },
                    )
                }

                snapshot.lotSnapshots.forEach { lotSnapshot ->
                    LotDetailRow(
                        lotSnapshot = lotSnapshot,
                        hasLivePrice = snapshot.hasLivePrice,
                        isEditing = editingLotId == lotSnapshot.lot.id,
                        isSelling = sellingLotId == lotSnapshot.lot.id,
                        onEdit = { editingLotId = lotSnapshot.lot.id; sellingLotId = null; showAddLot = false },
                        onSell = { sellingLotId = lotSnapshot.lot.id; editingLotId = null; showAddLot = false },
                        onCancel = { editingLotId = null; sellingLotId = null },
                        onSaveEdit = { shares, cost, date, note ->
                            onUpdateLot(lotSnapshot.lot.id, shares, cost, date, note)
                            editingLotId = null
                        },
                        onConfirmSell = { shares ->
                            onSellLot(lotSnapshot.lot.id, shares)
                            sellingLotId = null
                        },
                    )
                }

                if (showAddLot) {
                    LotForm(
                        title = "New Purchase Lot",
                        initialShares = "",
                        initialCost = "",
                        initialDate = "",
                        initialNote = "",
                        onSave = { shares, cost, date, note ->
                            onAddLot(shares, cost, date, note)
                            showAddLot = false
                        },
                        onCancel = { showAddLot = false }
                    )
                }
            }
        }
    }
}

@Composable
private fun AnalysisCard(title: String, items: List<String>, color: Color) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(title, style = MaterialTheme.typography.labelSmall, color = color, fontWeight = FontWeight.Bold)
        items.forEach { item ->
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Box(modifier = Modifier.size(4.dp).clip(RoundedCornerShape(2.dp)).background(color.copy(alpha = 0.5f)))
                Text(item, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurface)
            }
        }
    }
}

@Composable
private fun SellCaseSection(sellCase: SellCase) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(top = 8.dp)) {
        Text("Why consider reducing?", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Black, color = lossColor)
        sellCase.portfolioReason?.let { ReasonItem("Portfolio", it) }
        sellCase.performanceReason?.let { ReasonItem("Performance", it) }
        sellCase.fundamentalReason?.let { ReasonItem("Fundamental", it) }
        sellCase.opportunityReason?.let { ReasonItem("Opportunity", it) }
    }
}

@Composable
private fun ReasonItem(label: String, detail: String) {
    Column {
        Text(label, style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.onSurfaceVariant)
        Text(detail, style = MaterialTheme.typography.bodySmall)
    }
}

@Composable
private fun DetailItem(label: String, value: String, alignment: Alignment.Horizontal = Alignment.Start) {
    Column(horizontalAlignment = alignment) {
        Text(label, style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        Text(value, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.ExtraBold)
    }
}

@Composable
private fun LotDetailRow(
    lotSnapshot: LotSnapshot,
    hasLivePrice: Boolean,
    isEditing: Boolean,
    isSelling: Boolean,
    onEdit: () -> Unit,
    onSell: () -> Unit,
    onCancel: () -> Unit,
    onSaveEdit: (Double, Double, String?, String?) -> Unit,
    onConfirmSell: (Double) -> Unit,
) {
    val lot = lotSnapshot.lot
    val lotGainColor = if (lotSnapshot.unrealizedGainLoss >= 0) gainColor else lossColor

    Column(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
        if (isEditing) {
            LotForm(
                title = "Edit Lot",
                initialShares = lot.shares.toString(),
                initialCost = lot.costBasisPerShare.toString(),
                initialDate = lot.purchaseDate.orEmpty(),
                initialNote = lot.note.orEmpty(),
                onSave = onSaveEdit,
                onCancel = onCancel,
            )
        } else if (isSelling) {
            SellLotForm(
                maxShares = lot.shares,
                onConfirm = onConfirmSell,
                onCancel = onCancel,
            )
        } else {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        "${formatNumber(lot.shares, 2)} @ ${formatCurrency(lot.costBasisPerShare)}",
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Bold
                    )
                    lot.purchaseDate?.let {
                        Text("Bought $it", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                    }
                }
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(
                        formatGainLoss(
                            lotSnapshot.unrealizedGainLoss,
                            lotSnapshot.unrealizedGainLossPercent,
                            hasLivePrice,
                        ),
                        style = MaterialTheme.typography.labelMedium,
                        color = lotGainColor,
                        fontWeight = FontWeight.Bold
                    )
                    TextButton(onClick = onEdit) { Text("Edit", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold) }
                    TextButton(onClick = onSell) { Text("Sell", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold) }
                }
            }
        }
    }
}

@Composable
private fun LotForm(
    title: String,
    initialShares: String,
    initialCost: String,
    initialDate: String,
    initialNote: String,
    onSave: (Double, Double, String?, String?) -> Unit,
    onCancel: (() -> Unit)? = null,
) {
    var shares by remember(initialShares) { mutableStateOf(initialShares) }
    var cost by remember(initialCost) { mutableStateOf(initialCost) }
    var date by remember(initialDate) { mutableStateOf(initialDate) }
    var note by remember(initialNote) { mutableStateOf(initialNote) }

    Column(modifier = Modifier.fillMaxWidth().padding(8.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text(title, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Black)
        OutlinedTextField(
            value = shares,
            onValueChange = { shares = it },
            label = { Text("Shares") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
            shape = RoundedCornerShape(12.dp)
        )
        OutlinedTextField(
            value = cost,
            onValueChange = { cost = it },
            label = { Text("Price per share") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
            shape = RoundedCornerShape(12.dp)
        )
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            TextButton(
                onClick = {
                    val s = shares.toDoubleOrNull()
                    val c = cost.toDoubleOrNull()
                    if (s != null && s > 0 && c != null && c >= 0) {
                        onSave(s, c, date.takeIf { it.isNotBlank() }, note.takeIf { it.isNotBlank() })
                    }
                },
                modifier = Modifier.weight(1f).clip(RoundedCornerShape(12.dp)).background(MaterialTheme.colorScheme.primary)
            ) { 
                Text("Save", color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold) 
            }
            onCancel?.let { cancel ->
                TextButton(
                    onClick = cancel,
                    modifier = Modifier.weight(1f).clip(RoundedCornerShape(12.dp)).background(MaterialTheme.colorScheme.surfaceVariant)
                ) { 
                    Text("Cancel", color = MaterialTheme.colorScheme.onSurfaceVariant, fontWeight = FontWeight.Bold) 
                }
            }
        }
    }
}

@Composable
private fun SellLotForm(
    maxShares: Double,
    onConfirm: (Double) -> Unit,
    onCancel: () -> Unit,
) {
    var shares by remember { mutableStateOf(maxShares.toString()) }
    Column(modifier = Modifier.fillMaxWidth().padding(8.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text("Sell Position", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Black)
        OutlinedTextField(
            value = shares,
            onValueChange = { shares = it },
            label = { Text("Shares to sell") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
            shape = RoundedCornerShape(12.dp)
        )
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            TextButton(
                onClick = {
                    shares.toDoubleOrNull()?.takeIf { it > 0 && it <= maxShares }?.let(onConfirm)
                },
                modifier = Modifier.weight(1f).clip(RoundedCornerShape(12.dp)).background(MaterialTheme.colorScheme.error)
            ) { 
                Text("Confirm Sell", color = MaterialTheme.colorScheme.onError, fontWeight = FontWeight.Bold) 
            }
            TextButton(
                onClick = onCancel,
                modifier = Modifier.weight(1f).clip(RoundedCornerShape(12.dp)).background(MaterialTheme.colorScheme.surfaceVariant)
            ) { 
                Text("Cancel", color = MaterialTheme.colorScheme.onSurfaceVariant, fontWeight = FontWeight.Bold) 
            }
        }
    }
}
